<script setup lang="ts">
import { ref, watch } from 'vue'
import { useBandStore } from '../../stores/bandStore'
import { useUserStore } from '../../stores/userStore'
import api from '../../api'

const bandStore = useBandStore()
const userStore = useUserStore()

const visible = defineModel<boolean>('visible')
const newBandName = ref('')
const creatingBand = ref(false)
const bandErrorMessage = ref('')
const nameAvailable = ref<boolean | null>(null)
const nameChecking = ref(false)

let nameCheckTimeout: any = null
watch(newBandName, (name) => {
  nameAvailable.value = null
  if (nameCheckTimeout) clearTimeout(nameCheckTimeout)
  if (!name.trim()) return
  nameCheckTimeout = setTimeout(async () => {
    nameChecking.value = true
    try {
      const token = await userStore.user?.getIdToken()
      const response = await api.get(`/bands/check-name?name=${encodeURIComponent(name.trim())}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      nameAvailable.value = response.data.available
    } catch {
      nameAvailable.value = null
    } finally {
      nameChecking.value = false
    }
  }, 400)
})

const handleCreateBand = async () => {
  if (!newBandName.value) return
  if (nameAvailable.value === false) {
    bandErrorMessage.value = "This band name is already taken."
    return
  }
  creatingBand.value = true
  bandErrorMessage.value = ''
  try {
    await bandStore.createBand(newBandName.value)
    visible.value = false
    newBandName.value = ''
  } catch (error: any) {
    bandErrorMessage.value = error.response?.data || error.message
  } finally {
    creatingBand.value = false
  }
}
</script>

<template>
  <Dialog v-model:visible="visible" modal header="Create a New Band" :style="{ width: '25rem' }">
    <div class="flex flex-col gap-4 py-2">
      <div v-if="bandErrorMessage" class="p-3 text-sm font-bold text-amber-500 bg-red-50 rounded-lg border border-red-200">
        {{ bandErrorMessage }}
      </div>
      
      <div class="flex flex-col gap-2">
        <label for="bandName" class="font-semibold text-sm">Band Name</label>
        <InputText id="bandName" v-model="newBandName" class="flex-auto" autocomplete="off" placeholder="e.g. The Riffers" @keyup.enter="handleCreateBand" />
        <div v-if="newBandName.trim()" class="flex items-center gap-1 mt-1">
          <i v-if="nameChecking" class="pi pi-spin pi-spinner text-xs text-slate-400"></i>
          <span v-else-if="nameAvailable === true" class="text-xs text-teal-600"><i class="pi pi-check mr-1"></i>Available</span>
          <span v-else-if="nameAvailable === false" class="text-xs text-red-500"><i class="pi pi-times mr-1"></i>Already taken</span>
        </div>
      </div>
      <div class="flex justify-end gap-2 mt-2">
        <Button type="button" label="Cancel" severity="secondary" @click="visible = false" />
        <Button type="button" label="Create" :loading="creatingBand" @click="handleCreateBand" />
      </div>
    </div>
  </Dialog>
</template>
